import { Button } from '@/components/ui/Button';
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

interface SurveyPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: {
    title: string;
    description: string;
    questions: {
      text: string;
      type: 'multiple-choice' | 'open-ended' | 'multi-select';
      options?: string[];
    }[];
  };
}

export function SurveyPreviewModal({ isOpen, onClose, template }: SurveyPreviewModalProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Show the modal immediately when opened
      setIsVisible(true);
    } else {
      // Add a small delay before removing from DOM to allow for animation
      const timer = setTimeout(() => {
        setIsVisible(false);
      }, 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  if (!isOpen && !isVisible) return null;

  return (
    <div 
      className={cn(
        "fixed inset-0 bg-black/50 flex items-start justify-center p-4 z-50 overflow-y-auto transition-opacity duration-200",
        isOpen ? "opacity-100" : "opacity-0"
      )}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className={cn(
          "bg-white dark:bg-gray-800 rounded-lg w-full max-w-2xl my-8 transition-all duration-200",
          isOpen ? "translate-y-0 opacity-100" : "-translate-y-4 opacity-0"
        )}
      >
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Survey Preview</h2>
          <button
            onClick={onClose}
            className="text-gray-500 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg p-1 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-4 max-h-[calc(100vh-16rem)] overflow-y-auto">
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">{template.title}</h3>
              {template.description && (
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{template.description}</p>
              )}
            </div>

            <div className="space-y-8">
              {template.questions.map((question, index) => (
                <div key={index} className="space-y-4">
                  <p className="text-base font-medium text-gray-900 dark:text-white">
                    {index + 1}. {question.text}
                  </p>

                  {question.type === 'multiple-choice' ? (
                    <div className="space-y-2">
                      {question.options?.map((option, optionIndex) => (
                        <label
                          key={optionIndex}
                          className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                        >
                          <input
                            type="radio"
                            name={`question-${index}`}
                            className="h-4 w-4 text-blue-600 dark:text-blue-400 border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                            disabled
                          />
                          <span className="text-sm text-gray-900 dark:text-white">{option}</span>
                        </label>
                      ))}
                    </div>
                  ) : question.type === 'multi-select' ? (
                    <div className="space-y-2">
                      {question.options?.map((option, optionIndex) => (
                        <label
                          key={optionIndex}
                          className="flex items-center space-x-3 p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded text-blue-600 dark:text-blue-400 border-gray-300 dark:border-gray-600 focus:ring-blue-500"
                            disabled
                          />
                          <span className="text-sm text-gray-900 dark:text-white">{option}</span>
                        </label>
                      ))}
                    </div>
                  ) : (
                    <textarea
                      disabled
                      placeholder="Enter your answer here..."
                      rows={3}
                      className="block w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-blue-500 focus:ring-blue-500 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white disabled:opacity-50 disabled:cursor-not-allowed resize-none"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <div className="flex justify-end">
            <Button onClick={onClose}>Close Preview</Button>
          </div>
        </div>
      </div>
    </div>
  );
}